import {
  OPENINGS_EDDIES_HANDLE,
  OPENINGS_EDITION_STUDIO_HANDLE,
} from "@/consts";

/**
 * Stable identifier for each Openings business this app surfaces. Adding an
 * acquired business is one entry in `BUSINESSES` plus its env secrets — no
 * adapter, page, or component changes.
 */
export type EditionBusinessKey = "eddies" | "edition-studio";

export type BusinessBranding = {
  /** Customer-facing name. */
  displayName: string;
  /**
   * "eddies" renders under the Eddie's Edition brand (an absorbed
   * acquisition); "own-brand" gets its own name, badge, and JSON-LD org.
   */
  presentsAs: "eddies" | "own-brand";
  /** Shown on location tags and stylist pages. Required for "own-brand". */
  badgeLabel?: string;
  /** JSON-LD `@id` for this business's Organization node. */
  organizationId: string;
  /** Origin used to build canonical URLs for this business's pages. */
  canonicalHost: string;
  /** The business's own marketing site, when it has one. */
  externalSiteUrl?: string;
};

export type BusinessConfig = {
  key: EditionBusinessKey;
  /** Openings slug used to resolve the business record. */
  handle: string;
  branding: BusinessBranding;
  /**
   * Suffix for this business's welcome-coupon env vars:
   * `OPENINGS_OFFER_ID_<suffix>` and `OPENINGS_OFFER_SECRET_<suffix>`.
   */
  offerEnvSuffix: string;
};

export const DEFAULT_EDITION_BUSINESS: EditionBusinessKey = "eddies";

export const BUSINESSES: Record<EditionBusinessKey, BusinessConfig> = {
  eddies: {
    key: "eddies",
    handle: OPENINGS_EDDIES_HANDLE,
    branding: {
      displayName: "Eddie's Edition",
      presentsAs: "eddies",
      organizationId: "https://www.eddiesedition.com/#organization",
      canonicalHost: "https://www.eddiesedition.com",
    },
    offerEnvSuffix: "EDDIES",
  },
  "edition-studio": {
    key: "edition-studio",
    handle: OPENINGS_EDITION_STUDIO_HANDLE,
    branding: {
      displayName: "Edition Studio",
      presentsAs: "own-brand",
      badgeLabel: "Edition Studio",
      organizationId: "https://www.eddiesedition.com/#edition-studio",
      canonicalHost: "https://www.eddiesedition.com",
      externalSiteUrl: "https://www.editionstudio.com",
    },
    offerEnvSuffix: "STUDIO",
  },
};

export const BUSINESS_KEYS = Object.keys(BUSINESSES) as EditionBusinessKey[];

export const isEditionBusinessKey = (
  value: string | null | undefined,
): value is EditionBusinessKey =>
  !!value && Object.prototype.hasOwnProperty.call(BUSINESSES, value);

export const toEditionBusinessKey = (
  value: string | null | undefined,
): EditionBusinessKey =>
  isEditionBusinessKey(value) ? value : DEFAULT_EDITION_BUSINESS;
